/**
 * React Query hooks for tenant settings
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantSettingsApi } from '../services/tenantSettingsApi';
import type { UserManagementTenantSettingsUpdate } from '../services/tenantSettingsApi';
import { useAuth } from '../auth';
import type { TenantSettingsUpdate } from '../types/tenantSettings';
import { PERSONA_QUERY_KEY } from './usePersona';

const QUERY_KEY = ['tenant-settings'];
const LOCALE_SETTINGS_QUERY_KEY = ['tenant-locale-settings'];
const USER_DOMAINS_QUERY_KEY = ['tenant-settings', 'user-domains'];
const USER_MANAGEMENT_SETTINGS_KEY = ['user-management-tenant-settings'];

export function useTenantSettings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: tenantSettingsApi.getSettings,
    enabled: !!user?.tenant_id,
  });
}

/**
 * Hook for fetching public locale settings (accessible by any authenticated user).
 * Use this instead of useTenantSettings when you only need locale/format settings.
 */
export function useTenantLocaleSettings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: LOCALE_SETTINGS_QUERY_KEY,
    queryFn: tenantSettingsApi.getLocaleSettings,
    enabled: !!user?.tenant_id,
  });
}

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TenantSettingsUpdate) => tenantSettingsApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // The persona flag (sells_freight_services) lives in tenant settings;
      // refresh any usePersona() consumers when settings change.
      queryClient.invalidateQueries({ queryKey: PERSONA_QUERY_KEY });
    },
  });
}

export function useTenantUserDomains() {
  const { user } = useAuth();

  return useQuery({
    queryKey: USER_DOMAINS_QUERY_KEY,
    queryFn: tenantSettingsApi.getUserDomains,
    enabled: !!user?.tenant_id,
  });
}

export function useUserManagementTenantSettings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: USER_MANAGEMENT_SETTINGS_KEY,
    queryFn: tenantSettingsApi.getUserManagementSettings,
    enabled: !!user?.tenant_id,
  });
}

export function useUpdateUserManagementTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserManagementTenantSettingsUpdate) => tenantSettingsApi.updateUserManagementSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_MANAGEMENT_SETTINGS_KEY });
    },
  });
}
